import React, { useState, useEffect, useRef } from 'react';
import { Pencil, X, Save, RotateCcw, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { toast } from 'sonner';

/**
 * EditableText
 *
 * Renders text from the content store (or falls back to `defaultValue`).
 * For admin users, shows a tiny pencil button that opens an inline editor modal.
 *
 * Props:
 *   id            — unique content key (e.g. 'home.hero.title')
 *   defaultValue  — hardcoded fallback text shown when no override exists
 *   as            — HTML tag to render ('span', 'h1', 'p', 'div', ...). Default 'span'.
 *   multiline     — render <textarea> in the editor (longer body copy). Default false.
 *   className     — passes through to the rendered element
 *   children      — ignored (defaultValue is the source of truth)
 *   ...rest       — forwarded to the rendered element
 */
const EditableText = ({
  id,
  defaultValue = '',
  as = 'span',
  multiline = false,
  className = '',
  preserveLineBreaks = true,
  paragraphs = false,
  markdown = false,
  ...rest
}) => {
  const { user, token } = useAuth();
  const { get, save, reset, editMode } = useContent();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef(null);

  // Pencil icon only renders when the admin has explicitly enabled edit mode
  // via the toggle in /admin. Logged-out users and non-admins never see it;
  // admins viewing the site with edit mode off see the page just like a
  // regular visitor.
  const isAdmin = Boolean(user?.is_admin);
  const canEdit = isAdmin && editMode;
  const value = get(id, defaultValue);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, value]);

  const handleSave = async () => {
    setBusy(true);
    try {
      await save(id, draft, token);
      toast.success('Saved');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this text to its default? This removes your custom version.')) return;
    setBusy(true);
    try {
      await reset(id, token);
      toast.success('Reset to default');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset');
    } finally {
      setBusy(false);
    }
  };

  const Tag = as;

  // Inline markdown for admin-edited rich text. Keep it tiny on purpose:
  //   **word**  → <strong className="golden-text">word</strong>   (golden bold)
  //   __word__  → <strong>word</strong>                            (plain bold)
  //   _word_    → <em>word</em>                                    (italic)
  // Renders a plain string as an array of React nodes.
  const renderInlineMarkdown = (text) => {
    if (!markdown || !text) return text;
    const out = [];
    // Tokenise by the three patterns
    const regex = /(\*\*[^*]+\*\*|__[^_]+__|_[^_]+_)/g;
    let lastIndex = 0;
    let m;
    let key = 0;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > lastIndex) out.push(text.slice(lastIndex, m.index));
      const tok = m[0];
      if (tok.startsWith('**')) {
        out.push(<strong key={`md-${key++}`} className="golden-text">{tok.slice(2, -2)}</strong>);
      } else if (tok.startsWith('__')) {
        out.push(<strong key={`md-${key++}`}>{tok.slice(2, -2)}</strong>);
      } else if (tok.startsWith('_')) {
        out.push(<em key={`md-${key++}`}>{tok.slice(1, -1)}</em>);
      }
      lastIndex = m.index + tok.length;
    }
    if (lastIndex < text.length) out.push(text.slice(lastIndex));
    return out;
  };

  // Render text with preserved line breaks for multiline content.
  // If `paragraphs` is true, split on blank lines and render each chunk as a <p>.
  let renderedText;
  if (paragraphs && value) {
    const blocks = value.split(/\n\s*\n/).filter((b) => b.trim() !== '');
    renderedText = blocks.map((block, i) => (
      <p key={`para-${i}`} className={i === 0 ? '' : 'mt-4'}>
        {block.split('\n').map((line, j, arr) => (
          <React.Fragment key={j}>
            {renderInlineMarkdown(line)}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    ));
  } else if (preserveLineBreaks && value && value.includes('\n')) {
    renderedText = value.split('\n').map((line, i, arr) => (
      <React.Fragment key={i}>
        {renderInlineMarkdown(line)}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  } else {
    renderedText = renderInlineMarkdown(value);
  }

  if (!canEdit) {
    return (
      <Tag className={className} {...rest}>
        {renderedText}
      </Tag>
    );
  }

  return (
    <>
      <Tag
        className={`${className} group/editable relative inline-block`}
        data-content-key={id}
        {...rest}
      >
        {renderedText}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
          className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full border border-amber-300/40 bg-amber-400/[0.08] text-amber-200/90 hover:bg-amber-400/[0.2] hover:text-amber-50 hover:border-amber-300/80 transition opacity-60 hover:opacity-100 align-middle"
          aria-label={`Edit ${id}`}
          title={`Edit · ${id}`}
          data-testid={`editable-text-edit-${id}`}
        >
          <Pencil size={11} />
        </button>
      </Tag>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
          data-testid="editable-text-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-2xl card-mystical rounded-2xl border border-primary/40 p-6 shadow-2xl" style={{ background: 'rgba(20,8,2,0.96)' }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-[11px] font-heading tracking-[0.35em] text-primary/55 uppercase mb-1">
                  Edit content
                </div>
                <code className="text-xs text-primary/75 break-all">{id}</code>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-primary/60 hover:text-primary transition-colors shrink-0"
                aria-label="Close editor"
                data-testid="editable-text-close"
              >
                <X size={18} />
              </button>
            </div>

            {multiline ? (
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={Math.min(16, Math.max(4, draft.split('\n').length + 1))}
                className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary/90 focus:outline-none focus:border-primary/60 leading-relaxed"
                data-testid="editable-text-textarea"
              />
            ) : (
              <input
                ref={textareaRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary/90 focus:outline-none focus:border-primary/60"
                data-testid="editable-text-input"
              />
            )}

            <div className="mt-2 flex items-center justify-between text-[11px] text-primary/50">
              <span>{draft.length} characters</span>
              <span>
                {value !== defaultValue
                  ? <span className="text-amber-300/80">Custom override active</span>
                  : <span className="text-primary/40">Showing default</span>}
              </span>
            </div>
            {markdown && (
              <div className="mt-2 text-[11px] text-primary/50 italic" data-testid="editable-text-markdown-hint">
                Tip — wrap text with <code className="text-amber-300/80">**Shiva**</code> for golden bold,
                {' '}<code className="text-primary/70">__word__</code> for plain bold, or
                {' '}<code className="text-primary/70">_word_</code> for italics.
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={busy || value === defaultValue}
                className="inline-flex items-center gap-2 px-3 py-2 text-xs text-primary/70 hover:text-primary/95 disabled:opacity-40 disabled:cursor-not-allowed transition"
                data-testid="editable-text-reset"
              >
                <RotateCcw size={13} /> Reset to default
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm text-primary/75 hover:text-primary"
                  data-testid="editable-text-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || draft === value}
                  className="ornate-button inline-flex items-center gap-2 px-5 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="editable-text-save"
                >
                  {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default EditableText;
